import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Clock, CheckCircle, XCircle, Users, Download } from "lucide-react";
import api from "../../api/axios";
import { getUser, isLoggedIn } from "../../utils/auth";

export default function AttendanceManagement() {
    const { id } = useParams(); // project_id
    const [activeTab, setActiveTab] = useState("today");
    const [members, setMembers] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [history, setHistory] = useState([]);
    const [report, setReport] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
    const [showCheckInModal, setShowCheckInModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [geoLocation, setGeoLocation] = useState({ latitude: null, longitude: null });
    const [currentUser, setCurrentUser] = useState(null);

    // Fetch data on mount and when tab changes
    useEffect(() => {
        // Get current logged-in user
        if (!isLoggedIn()) {
            setError("You must be logged in to access attendance");
            return;
        }
        const user = getUser();
        setCurrentUser(user);

        if (activeTab === "today") {
            fetchTodayAttendance();
        } else if (activeTab === "history") {
            fetchHistory();
        } else if (activeTab === "report") {
            fetchReport();
        }
    }, [activeTab, selectedDate, id]);

    // Get geolocation on component mount
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setGeoLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                },
                (error) => console.log("Geolocation error:", error)
            );
        }
    }, []);

    // Fetch today's attendance
    const fetchTodayAttendance = async () => {
        try {
            setLoading(true);

            const response = await api.get(`/api/attendance/today/${id}`);

            setAttendance(response.data.data || []);
            setMembers(response.data.data || []);
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Failed to load attendance";
            console.error("❌ Error fetching attendance:", {
                message: errorMsg,
                status: err.response?.status,
                data: err.response?.data,
                fullError: err
            });
            setError(`API Error: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch attendance history
    const fetchHistory = async () => {
        try {
            setLoading(true);
            console.log(`🔵 API CALL: GET /api/attendance/history/${id}`);
            console.log(`📍 Full URL: https://api-v1.htcl.co.in/api/attendance/history/${id}`);
            console.log(`🔐 Token: ${localStorage.getItem('token') ? 'Present' : 'Missing'}`);

            const response = await api.get(`/api/attendance/history/${id}`);
            console.log("✅ Attendance History Response:", response.data);
            console.log("📊 Data Count:", response.data.data?.length || 0);

            setHistory(response.data.data || []);
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Failed to load history";
            console.error("❌ Error fetching history:", {
                message: errorMsg,
                status: err.response?.status,
                data: err.response?.data,
                fullError: err
            });
            setError(`API Error: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch attendance report
    const fetchReport = async () => {
        try {
            setLoading(true);
            console.log(`🔵 API CALL: GET /api/attendance/report?attendance_date=${selectedDate}`);
            console.log(`📍 Full URL: https://api-v1.htcl.co.in/api/attendance/report?attendance_date=${selectedDate}`);
            console.log(`🔐 Token: ${localStorage.getItem('token') ? 'Present' : 'Missing'}`);

            const response = await api.get(`/api/attendance/report?attendance_date=${selectedDate}`);
            console.log("✅ Attendance Report Response:", response.data);
            console.log("📊 Data Count:", response.data.data?.length || 0);

            setReport(response.data.data || []);
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Failed to load report";
            console.error("❌ Error fetching report:", {
                message: errorMsg,
                status: err.response?.status,
                data: err.response?.data,
                fullError: err
            });
            setError(`API Error: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle check-in
    const handleCheckIn = async (memberId) => {
        try {
            // Validate authentication
            if (!isLoggedIn()) {
                setError("You must be logged in to check in");
                return;
            }

            // Get the member data to validate assignment
            const rawAttendance = attendance.find(m => m.id === memberId);
            if (!rawAttendance) {
                setError("Member not found in attendance list");
                return;
            }

            // Validate member is assigned to project (has valid project_team_member_id)
            if (!rawAttendance.project_team_member_id) {
                setError(`Member is not assigned to this project and cannot check in.`);
                return;
            }

            // Validate member doesn't already have an active session
            const hasActiveSession = rawAttendance.sessions && rawAttendance.sessions.length > 0
                ? !rawAttendance.sessions[rawAttendance.sessions.length - 1].check_out
                : false;

            if (hasActiveSession) {
                setError(`Member is already checked in.`);
                return;
            }

            const payload = {
                project_id: parseInt(id),
                project_team_member_id: rawAttendance.project_team_member_id,
                latitude: geoLocation.latitude || "0",
                longitude: geoLocation.longitude || "0",
            };
            console.log(`🔵 API CALL: POST /api/attendance/checkin`);
            console.log(`📍 Full URL: https://api-v1.htcl.co.in/api/attendance/checkin`);
            console.log(`🔐 Token: ${localStorage.getItem('token') ? 'Present' : 'Missing'}`);
            console.log(`📦 Payload:`, payload);

            const response = await api.post("/api/attendance/checkin", payload);
            console.log("✅ Check-in successful:", response.data);
            setShowCheckInModal(false);
            setSelectedMember(null);
            fetchTodayAttendance();
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Failed to check in";
            console.error("❌ Error checking in:", {
                message: errorMsg,
                status: err.response?.status,
                data: err.response?.data,
                fullError: err
            });
            setError(`API Error: ${errorMsg}`);
        }
    };

    // Handle check-out
    const handleCheckOut = async (sessionId) => {
        try {
            if (!isLoggedIn()) {
                setError("You must be logged in to check out");
                return;
            }

            const payload = {
                session_id: sessionId,
                latitude: geoLocation.latitude || "0",
                longitude: geoLocation.longitude || "0",
            };
            // console.log(`🔵 API CALL: POST /api/attendance/checkout`);
            // console.log(`📍 Full URL: https://api-v1.htcl.co.in/api/attendance/checkout`);
            // console.log(`🔐 Token: ${localStorage.getItem('token') ? 'Present' : 'Missing'}`);
            console.log(`📦 Payload:`, payload);

            const response = await api.post("/api/attendance/checkout", payload);
            // console.log("✅ Check-out successful:", response.data);
            fetchTodayAttendance();
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Failed to check out";
            console.error("❌ Error checking out:", {
                message: errorMsg,
                status: err.response?.status,
                data: err.response?.data,
                fullError: err
            });
            setError(`API Error: ${errorMsg}`);
        }
    };

    // Transform attendance data to flat structure for easy display
    const transformAttendanceData = (data) => {
        return data.map(item => {
            const latestSession = item.sessions && item.sessions.length > 0
                ? item.sessions[item.sessions.length - 1]
                : null;

            return {
                id: item.id, // attendance_day_id
                project_team_member_id: item.project_team_member_id,
                member_name: item.member?.member_name || "Unknown",
                designation: item.member?.designation || "—",
                mobile: item.member?.mobile,
                email: item.member?.email,
                check_in: latestSession?.check_in
                    ? new Date(latestSession.check_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                    : "—",
                check_out: latestSession?.check_out
                    ? new Date(latestSession.check_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                    : "—",
                hours: item.total_hours || "0.00",
                status: item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || "Absent",
                sessions: item.sessions || [],
                attendance_date: item.attendance_date,
                // For check-in/out logic
                has_active_session: latestSession && !latestSession.check_out,
                latest_session: latestSession,
                all_sessions: item.sessions,
            };
        });
    };

    // Transform attendance data
    const transformedAttendance = transformAttendanceData(attendance);

    // Calculate stats using transformed data
    const stats = {
        totalMembers: transformedAttendance.length,
        presentToday: transformedAttendance.filter(m => m.status === "Present").length,
        absentToday: transformedAttendance.filter(m => m.status === "Absent").length,
        currentlyIn: transformedAttendance.filter(m => m.has_active_session).length,
        totalHours: transformedAttendance.reduce((sum, m) => {
            const hours = parseFloat(m.hours);
            return sum + (isNaN(hours) ? 0 : hours);
        }, 0).toFixed(2),
    };

    return (


        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Attendance Management</h2>
                    <p className="text-sm text-gray-600">Track check-in / check-out, view history & generate reports</p>
                </div>
                <div className="flex gap-3">
                    <button className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        <Download size={18} />
                        Export Report
                    </button>
                    <button
                        onClick={() => setShowCheckInModal(true)}
                        disabled={!isLoggedIn() || transformedAttendance.filter(m => m.project_team_member_id && !m.has_active_session).length === 0}
                        title={!isLoggedIn() ? "You must be logged in" : transformedAttendance.filter(m => m.project_team_member_id && !m.has_active_session).length === 0 ? "No members available for check-in" : "Check-In Member"}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        <Plus size={18} />
                        Check-In Member
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="bg-purple-100 p-3 rounded-lg">
                            <Users className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Total Members</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.totalMembers}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="bg-green-100 p-3 rounded-lg">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Present Today</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.presentToday}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="bg-red-100 p-3 rounded-lg">
                            <XCircle className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Absent Today</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.absentToday}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="bg-blue-100 p-3 rounded-lg">
                            <Clock className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Currently In</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.currentlyIn}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                        <div className="bg-yellow-100 p-3 rounded-lg">
                            <Clock className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Total Hours Today</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.totalHours}h</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-2">
                {[
                    { id: "today", label: "Today", icon: "📅" },
                    { id: "checkin", label: "Check-In / Out", icon: "🕐" },
                    { id: "history", label: "History", icon: "📋" },
                    { id: "report", label: "Report", icon: "📊" },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === tab.id
                            ? "bg-blue-600 text-white"
                            : "text-gray-700 hover:bg-gray-100"
                            }`}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* Content Based on Tab */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading && activeTab !== "checkin" ? (
                    <div className="p-10 text-center text-gray-500">Loading...</div>
                ) : activeTab === "checkin" ? (
                    <div className="grid grid-cols-2 gap-6 p-6">
                        {/* Check-In Section */}
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <CheckCircle size={20} className="text-green-600" />
                                Check-In Member
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">Select a member and mark their check-in</p>

                            <div className="space-y-3 mb-6">
                                <p className="text-xs font-semibold text-gray-600 uppercase">SELECT MEMBER</p>
                                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                                    {transformedAttendance
                                        .filter(m => {
                                            // Only show members assigned to this project who don't have active session
                                            return m.project_team_member_id && !m.has_active_session;
                                        })
                                        .map((member) => (
                                            <button
                                                key={member.id}
                                                onClick={() => setSelectedMember(member)}
                                                className={`p-3 border rounded-lg text-left transition-all ${selectedMember?.id === member.id
                                                    ? "border-blue-500 bg-blue-50"
                                                    : "border-gray-300 hover:border-blue-300 hover:bg-gray-50"
                                                    }`}
                                            >
                                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm mb-2">
                                                    {member.member_name?.charAt(0).toUpperCase()}
                                                </div>
                                                <p className="font-medium text-gray-800 text-sm">{member.member_name}</p>
                                                <p className="text-xs text-gray-600">{member.designation}</p>
                                            </button>
                                        ))}
                                </div>
                                {transformedAttendance.filter(m => m.project_team_member_id && !m.has_active_session).length === 0 && (
                                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
                                        <p>⚠️ No members available for check-in. All members are either already checked in or not assigned to this project.</p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase block mb-2">Latitude</label>
                                    <input
                                        type="text"
                                        value={geoLocation.latitude || "0"}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase block mb-2">Longitude</label>
                                    <input
                                        type="text"
                                        value={geoLocation.longitude || "0"}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                                    />
                                </div>
                                <button
                                    onClick={() => selectedMember && handleCheckIn(selectedMember.id)}
                                    disabled={!selectedMember}
                                    className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center gap-2"
                                >
                                    <CheckCircle size={18} />
                                    Mark Check-In
                                </button>
                            </div>
                        </div>

                        {/* Active Sessions Section */}
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <Clock size={20} className="text-red-600" />
                                Active Sessions
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">Members currently checked in — click to check out</p>

                            {transformedAttendance.filter(m => m.has_active_session).length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    <p className="text-sm">No active sessions</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {transformedAttendance.filter(m => m.has_active_session).map((member) => {
                                        const checkInTime = new Date(member.latest_session?.check_in);
                                        const now = new Date();
                                        const elapsedMs = now - checkInTime;
                                        const hours = Math.floor(elapsedMs / 3600000);
                                        const minutes = Math.floor((elapsedMs % 3600000) / 60000);
                                        const seconds = Math.floor((elapsedMs % 60000) / 1000);
                                        const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

                                        return (
                                            <div key={member.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                                        <div className="w-3 h-3 bg-white rounded-full"></div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="font-medium text-gray-800">{member.member_name}</p>
                                                        <p className="text-xs text-gray-600">Checked in at {member.check_in}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between mb-3">
                                                    <p className="text-2xl font-bold text-blue-600">{timeStr}</p>
                                                </div>
                                                <button
                                                    onClick={() => handleCheckOut(member.latest_session?.id || member.id)}
                                                    className="w-full bg-red-600 text-white px-3 py-2 rounded font-semibold hover:bg-red-700 transition-colors text-sm"
                                                >
                                                    Check-Out
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                ) : activeTab === "today" ? (
                    <>
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-800">Today's Attendance — {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</h3>
                            <button className="text-blue-600 hover:text-blue-800 font-medium">🔄 Refresh</button>
                        </div>
                        {transformedAttendance.length === 0 ? (
                            <div className="p-10 text-center text-gray-500">No attendance data available</div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">#</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">MEMBER</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">DESIGNATION</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">CHECK-IN</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">CHECK-OUT</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">HOURS</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">STATUS</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">SESSIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transformedAttendance.map((member, index) => (
                                        <tr key={member.id} className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm text-gray-700 font-medium">{index + 1}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                                                        {member.member_name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-800">{member.member_name}</p>
                                                        <p className="text-xs text-gray-500">{member.designation}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-700">{member.designation}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700">{member.check_in}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700">{member.check_out}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700">{member.hours}h</td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${member.status === "Present"
                                                    ? "bg-green-100 text-green-700"
                                                    : member.status === "Half day"
                                                        ? "bg-yellow-100 text-yellow-700"
                                                        : "bg-red-100 text-red-700"
                                                    }`}>
                                                    {member.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-700">
                                                {member.sessions.length} session{member.sessions.length !== 1 ? "s" : ""}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                ) : activeTab === "history" ? (
                    <>
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-800">Attendance History</h3>
                            <div className="flex gap-3">
                                <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                                    <option>All Members</option>
                                    {transformedAttendance.map(m => (
                                        <option key={m.id} value={m.id}>{m.member_name}</option>
                                    ))}
                                </select>
                                <button className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium text-sm">Filter</button>
                            </div>
                        </div>
                        {history.length === 0 ? (
                            <div className="p-10 text-center text-gray-500">No history available</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">#</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">MEMBER</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">DATE</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">CHECK-IN</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">CHECK-OUT</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">TOTAL HOURS</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">SESSIONS</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">STATUS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transformAttendanceData(history).map((record, index) => (
                                            <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm text-gray-700 font-medium">{index + 1}</td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold">
                                                            {record.member_name?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-800">{record.member_name}</p>
                                                            <p className="text-xs text-gray-500">{record.designation}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-700">{record.attendance_date}</td>
                                                <td className="px-6 py-4 text-sm text-gray-700">{record.check_in}</td>
                                                <td className="px-6 py-4 text-sm text-gray-700">{record.check_out}</td>
                                                <td className="px-6 py-4 text-sm text-gray-700 font-medium">{record.hours}h</td>
                                                <td className="px-6 py-4 text-sm text-gray-700">
                                                    {record.sessions.length} session{record.sessions.length !== 1 ? "s" : ""}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${record.status === "Present"
                                                        ? "bg-green-100 text-green-700"
                                                        : record.status === "Half day"
                                                            ? "bg-yellow-100 text-yellow-700"
                                                            : "bg-red-100 text-red-700"
                                                        }`}>
                                                        {record.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                ) : activeTab === "report" ? (
                    <>
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="grid grid-cols-5 gap-4 items-end">
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase block mb-2">FROM DATE</label>
                                    <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase block mb-2">TO DATE</label>
                                    <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase block mb-2">MEMBER</label>
                                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                                        <option>All Members</option>
                                        {transformedAttendance.map(m => (
                                            <option key={m.id} value={m.id}>{m.member_name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase block mb-2">STATUS</label>
                                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                                        <option>All</option>
                                        <option>Present</option>
                                        <option>Absent</option>
                                        <option>Half Day</option>
                                    </select>
                                </div>
                                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-2 w-full">
                                    <Download size={16} />
                                    Generate
                                </button>
                            </div>
                        </div>
                        {report.length === 0 ? (
                            <div className="p-10 text-center text-gray-500">No report data available</div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-max">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">#</th>
                                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">MEMBER</th>
                                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">DATE</th>
                                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">TOTAL HOURS</th>
                                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">SESSIONS</th>
                                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">STATUS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transformAttendanceData(report).map((item, index) => (
                                                <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                                                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">{index + 1}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold">
                                                                {item.member_name?.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-800">{item.member_name}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-700">{item.attendance_date}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">{item.hours}h</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700">
                                                        {item.sessions.length} session{item.sessions.length !== 1 ? "s" : ""}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${item.status === "Present"
                                                            ? "bg-green-100 text-green-700"
                                                            : item.status === "Half day"
                                                                ? "bg-yellow-100 text-yellow-700"
                                                                : "bg-red-100 text-red-700"
                                                            }`}>
                                                            {item.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-8">
                                    <div className="text-center">
                                        <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Present</p>
                                        <p className="text-2xl font-bold text-green-600">{transformAttendanceData(report).filter(r => r.status === "Present").length}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Absent</p>
                                        <p className="text-2xl font-bold text-red-600">{transformAttendanceData(report).filter(r => r.status === "Absent").length}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Total Hrs</p>
                                        <p className="text-2xl font-bold text-orange-600">{transformAttendanceData(report).reduce((sum, r) => sum + parseFloat(r.hours), 0).toFixed(1)}h</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}

